'use client';
import React, { useEffect, useRef, useState } from 'react';

interface TextScrambleProps {
  text: string;
  duration?: number; // in ms
  interval?: number; // frame update interval in ms
  repeatInterval?: number; // time in ms to repeat the animation
  offset?: number; // delay before animation starts, in ms
}

const CHARSET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890!@#$%^&*()_+-=[]{}|;:,.<>?';

export const TextScramble: React.FC<TextScrambleProps> = ({
  text,
  duration = 1500,
  interval = 30,
  repeatInterval,
  offset = 0,
}) => {
  const [display, setDisplay] = useState('');
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const visibleRef = useRef<boolean>(true);
  const repeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const offsetTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to run the scramble animation, with offset
  const runScramble = React.useCallback(() => {
    // Clean up any pending animation or timeout
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (repeatTimeoutRef.current !== null) {
      clearTimeout(repeatTimeoutRef.current);
      repeatTimeoutRef.current = null;
    }
    if (offsetTimeoutRef.current !== null) {
      clearTimeout(offsetTimeoutRef.current);
      offsetTimeoutRef.current = null;
    }
    const totalFrames = Math.floor(duration / interval);
    const target = text.split('');
    const scrambled: string[] = new Array(target.length).fill('');

    const animate = (timestamp: number) => {
      if (startTimeRef.current === null) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const frame = Math.floor(elapsed / interval);
      if (frame > totalFrames) {
        setDisplay(text);
        // Schedule repeat if needed
        if (repeatInterval && visibleRef.current) {
          repeatTimeoutRef.current = setTimeout(() => {
            startTimeRef.current = null;
            runScramble();
          }, repeatInterval + offset);
        }
        return;
      }
      for (let i = 0; i < target.length; i++) {
        if (frame / totalFrames > i / target.length) {
          scrambled[i] = target[i];
        } else if (scrambled[i] !== target[i]) {
          scrambled[i] = CHARSET[Math.floor(Math.random() * CHARSET.length)];
        }
      }
      setDisplay(scrambled.join(''));
      rafRef.current = requestAnimationFrame(animate);
    };

    // Start after offset
    if (offset > 0) {
      offsetTimeoutRef.current = setTimeout(() => {
        setDisplay(''); // Only clear the display right before scramble starts
        startTimeRef.current = null;
        rafRef.current = requestAnimationFrame(animate);
      }, offset);
    } else {
      setDisplay('');
      startTimeRef.current = null;
      rafRef.current = requestAnimationFrame(animate);
    }
  }, [text, duration, interval, repeatInterval, offset]);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    const node = containerRef.current;
    visibleRef.current = true;

    // Visibility observer
    if (node && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          const isVisible = entries[0].isIntersecting;
          visibleRef.current = isVisible;
          if (isVisible && (repeatInterval || offset > 0)) {
            // Restart animation if it was paused
            runScramble();
          } else if (!isVisible) {
            // Pause any scheduled repeats
            if (rafRef.current !== null) {
              cancelAnimationFrame(rafRef.current);
              rafRef.current = null;
            }
            if (repeatTimeoutRef.current !== null) {
              clearTimeout(repeatTimeoutRef.current);
              repeatTimeoutRef.current = null;
            }
            if (offsetTimeoutRef.current !== null) {
              clearTimeout(offsetTimeoutRef.current);
              offsetTimeoutRef.current = null;
            }
          }
        },
        { threshold: 0.01 },
      );
      observer.observe(node);
    }

    // Initial run
    runScramble();

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (repeatTimeoutRef.current !== null) {
        clearTimeout(repeatTimeoutRef.current);
        repeatTimeoutRef.current = null;
      }
      if (offsetTimeoutRef.current !== null) {
        clearTimeout(offsetTimeoutRef.current);
        offsetTimeoutRef.current = null;
      }
      if (observer && node) {
        observer.unobserve(node);
      }
      startTimeRef.current = null;
    };
  }, [text, duration, interval, repeatInterval, offset, runScramble]);

  return <span ref={containerRef}>{display}</span>;
};
